// src/config/environment.ts
export const API_CONFIG = {
  // Fix production BASE_URL for Azure Static Web Apps
  BASE_URL: process.env.NODE_ENV === 'development' 
    ? (process.env.REACT_APP_API_URL || 'http://localhost:7071')
    : window.location.origin, // ✅ Use current origin in production
  
  ENDPOINTS: {
    GET_TOKEN: '/api/get-token',  
    SAML_SP_INIT: '/api/saml-sp-init',
    SAML_ACS: '/api/saml-acs',
    MARKETPLACE_LANDING: '/api/marketplace-landing',
    MARKETPLACE_WEBHOOK: '/api/marketplace-webhook',
    MARKETPLACE_ACTIVATION: '/api/marketplace-activation'
  }
};

export const isDevelopment = process.env.NODE_ENV === 'development';
export const isProduction = process.env.NODE_ENV === 'production';


// Enhanced environment configuration for SAML (additive, not replacing)
export interface SamlEnvironmentConfig {
  name: string;
  baseUrl: string;
  samlConfig: {
    issuer: string;
    acsUrl: string;
  };
}

// Add marketplace configuration interface
export interface MarketplaceEnvironmentConfig {
  name: string;
  baseUrl: string;
  marketplaceConfig: {
    landingPageUrl: string;
    webhookUrl: string;
    publisherId: string;
    offerId: string;
  };
}

export function getSamlEnvironmentConfig(): SamlEnvironmentConfig {
  const origin = window.location.origin;
  
  if (isDevelopment) {
    return {
      name: 'Development',
      baseUrl: API_CONFIG.BASE_URL, // Use your existing BASE_URL logic
      samlConfig: {
        issuer: 'http://localhost:7071',
        acsUrl: 'http://localhost:7071/api/saml-acs'
      }
    };
  }


  // Production
  return {
    name: 'Production',
    baseUrl: origin, // In staging/production, API calls go to same origin
    samlConfig: {
      issuer: origin,
      acsUrl: `${origin}/api/saml-acs`
    }
  };
}

// Add marketplace environment configuration
export function getMarketplaceEnvironmentConfig(): MarketplaceEnvironmentConfig {
  const origin = window.location.origin;
  
  if (isDevelopment) {
    return {
      name: 'Development',
      baseUrl: API_CONFIG.BASE_URL,
      marketplaceConfig: {
        landingPageUrl: 'http://localhost:7071/api/marketplace-landing',
        webhookUrl: 'http://localhost:7071/api/marketplace-webhook',
        publisherId: process.env.REACT_APP_MARKETPLACE_PUBLISHER_ID || 'trasksolutionsas1739201045321', // HARDCODED
        offerId: process.env.REACT_APP_MARKETPLACE_OFFER_ID || 'appconfig001' // HARDCODED
      }
    };
  }

  // Production
  return {
    name: 'Production',
    baseUrl: origin,
    marketplaceConfig: {
      landingPageUrl: `${origin}/api/marketplace-landing`,
      webhookUrl: `${origin}/api/marketplace-webhook`,
      publisherId: process.env.REACT_APP_MARKETPLACE_PUBLISHER_ID || 'your-publisher-id',
      offerId: process.env.REACT_APP_MARKETPLACE_OFFER_ID || 'your-offer-id'
    }
  };
}

// Helper function to get the full API URL (works with your existing pattern)
export function getApiUrl(endpoint: string): string {
  // In development, use full URL with BASE_URL
  if (isDevelopment && API_CONFIG.BASE_URL) {
    return `${API_CONFIG.BASE_URL}${endpoint}`;
  }
  
  // In production/staging, just use the endpoint (SWA routes /api/* to Functions)
  return endpoint;
}

// Convenience functions that work with your existing setup
export const getTokenUrl = () => getApiUrl(API_CONFIG.ENDPOINTS.GET_TOKEN);
export const getSamlSpInitUrl = () => getApiUrl(API_CONFIG.ENDPOINTS.SAML_SP_INIT);
export const getSamlAcsUrl = () => getApiUrl(API_CONFIG.ENDPOINTS.SAML_ACS);

// Add marketplace convenience functions
export const getMarketplaceLandingUrl = () => getApiUrl(API_CONFIG.ENDPOINTS.MARKETPLACE_LANDING);
export const getMarketplaceWebhookUrl = () => getApiUrl(API_CONFIG.ENDPOINTS.MARKETPLACE_WEBHOOK);
export const getMarketplaceActivationUrl = () => getApiUrl(API_CONFIG.ENDPOINTS.MARKETPLACE_ACTIVATION);

// Export SAML config for components that need it
export const SAML_CONFIG = getSamlEnvironmentConfig();

// Export marketplace config for components that need it
export const MARKETPLACE_CONFIG = getMarketplaceEnvironmentConfig();

// Debugging helper that doesn't disrupt existing functionality
export function logEnvironmentInfo(includeExisting = true) {
  const baseInfo = {
    nodeEnv: process.env.NODE_ENV,
    isDevelopment,
    isProduction,
    hostname: window.location.hostname,
    origin: window.location.origin
  };

  if (includeExisting) {
    console.log('[Environment] Existing API Config:', {
      ...baseInfo,
      apiBaseUrl: API_CONFIG.BASE_URL,
      getTokenUrl: getTokenUrl()
    });
  }

  console.log('[Environment] SAML Config:', {
    ...baseInfo,
    samlEnvironment: SAML_CONFIG.name,
    samlBaseUrl: SAML_CONFIG.baseUrl,
    samlIssuer: SAML_CONFIG.samlConfig.issuer,
    samlAcsUrl: SAML_CONFIG.samlConfig.acsUrl,
    samlSpInitUrl: getSamlSpInitUrl()
  });

  console.log('[Environment] Marketplace Config:', {
    ...baseInfo,
    marketplaceEnvironment: MARKETPLACE_CONFIG.name,
    marketplaceBaseUrl: MARKETPLACE_CONFIG.baseUrl,
    landingPageUrl: MARKETPLACE_CONFIG.marketplaceConfig.landingPageUrl,
    webhookUrl: MARKETPLACE_CONFIG.marketplaceConfig.webhookUrl,
    publisherId: MARKETPLACE_CONFIG.marketplaceConfig.publisherId,
    offerId: MARKETPLACE_CONFIG.marketplaceConfig.offerId
  });
}